import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapWagmiError } from '@helpers/common/error.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';

import showToast from '@components/common/Toast';

import { CHAIN_ID } from '@constants/common/chain.constants';
import { SuccessMessages } from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';
import { FUSION_NFT } from '@constants/fusion/contracts.constants';

import abi from '@abi/fusion/nftFusion';

export const useGetTransferRole = (chainId?: CHAIN_ID) => {
  return useReadContract({
    address: FUSION_NFT,
    abi,
    functionName: 'TRANSFER_ROLE',
    chainId,
    query: {
      enabled: !!chainId,
    },
  });
};

export const useGetRoleMemberCount = (chainId?: CHAIN_ID) => {
  const { data: transferRole } = useGetTransferRole(chainId);
  return useReadContract({
    address: FUSION_NFT,
    abi,
    functionName: 'getRoleMemberCount',
    args: transferRole ? [transferRole] : undefined,
    chainId,
    query: {
      enabled: !!chainId && !!transferRole,
    },
  });
};

export const useGetRoleMember = (chainId?: CHAIN_ID) => {
  const { data: roleMemberCount, refetch } = useGetRoleMemberCount(chainId);
  const { data: transferRole } = useGetTransferRole(chainId);

  const whitelistArray = useMemo(() => {
    if (!roleMemberCount) return [];

    return Array.from({ length: Number(roleMemberCount) }, (_, i) => BigInt(i));
  }, [roleMemberCount]);

  const { data, isPending, ...rest } = useReadContracts({
    contracts:
      whitelistArray.length && chainId
        ? whitelistArray.map((index) => ({
            address: FUSION_NFT,
            abi,
            functionName: 'getRoleMember',
            args: [transferRole, index],
            chainId,
          }))
        : [],
    query: {
      enabled: !!chainId && !!whitelistArray.length && !!transferRole,
      select: (res) => res as string[],
    },
    allowFailure: false,
  });

  return {
    ...rest,
    data,
    refetch,
    isPending: !!chainId && !!whitelistArray.length ? isPending : false,
  };
};

type SafeTransferFromParams = {
  to: Address;
  value: bigint;
};

export const useSafeTransferFrom = (handleUpdate?: () => Promise<void>) => {
  const { chainId, account } = useAuth();
  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        if (handleUpdate) await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Sent,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useSafeTransferFrom:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async (transferData: SafeTransferFromParams) => {
      await writeContractAsync({
        abi,
        address: FUSION_NFT,
        functionName: 'safeTransferFrom',
        chainId,
        args: [
          account as Address,
          transferData.to,
          BigInt(0),
          transferData.value,
          '0x',
        ],
        account,
      });
    },
    [chainId, writeContractAsync, account],
  );

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};

export const useGetRoleMembers = (role?: `0x${string}`, chainId?: CHAIN_ID) => {
  return useReadContract({
    address: FUSION_NFT,
    abi,
    functionName: 'getRoleMembers',
    args: role ? [role] : undefined,
    chainId,
    query: {
      enabled: !!chainId && !!role,
    },
  });
};

export const useHasRole = (
  role?: `0x${string}`,
  account?: Address,
  chainId?: CHAIN_ID,
) => {
  return useReadContract({
    address: FUSION_NFT,
    abi,
    functionName: 'hasRole',
    args: role && account ? [role, account] : undefined,
    chainId,
    query: {
      enabled: !!chainId && !!role && !!account,
    },
  });
};

export const useGetRoleAdmin = (role?: `0x${string}`, chainId?: CHAIN_ID) => {
  return useReadContract({
    address: FUSION_NFT,
    abi,
    functionName: 'getRoleAdmin',
    args: role ? [role] : undefined,
    chainId,
    query: {
      enabled: !!chainId && !!role,
    },
  });
};

export const useGrantRole = () => {
  const { chainId, account } = useAuth();
  const queryClient = useQueryClient();

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await queryClient.invalidateQueries();

        showToast(
          {
            title: SuccessMessages.Updated,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useGrantRole:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = async ({
    role,
    grantee,
  }: {
    role: `0x${string}`;
    grantee: Address;
  }) => {
    await writeContractAsync({
      abi,
      address: FUSION_NFT,
      functionName: 'grantRole',
      chainId,
      args: [role, grantee],
      account,
    });
  };

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};
